import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import stringUtils from '../../../../qti/utils/StringUtils';
import scorableItemNumberingService from '../../../../services/questionView/ScorableItemNumberingService';
import matchInteractionA11yService from '../../../../services/matchInteractionA11yService';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../services/a11yIIBScreenReaderService';
import styles from './matchInteraction.scss';

const RadioButton = (props) => {
    const {
        checked,
        columnChoiceIdentifier,
        handleUserResponse,
        isInverted,
        isReadOnly,
        modelIdentifier,
        rowChoiceIdentifier,
        questionId,
    } = props;

    const inputRef = useRef(null);
    const instanceId = stringUtils.getUID();
    const nameIdentifier = isInverted ? columnChoiceIdentifier : rowChoiceIdentifier;
    const name = `${nameIdentifier}-${modelIdentifier}-${questionId}`;
    const id = `${name}-${instanceId}`;
    const value = `${rowChoiceIdentifier} ${columnChoiceIdentifier}`;
    const labelledby = `rowChoice_${rowChoiceIdentifier} columnChoice_${columnChoiceIdentifier}`;

    const navigateToScorableItem = () => {
        scorableItemNumberingService.navigateToScorableItem(questionId, modelIdentifier, nameIdentifier, true);
    };

    // Only attach keyboard handler when accessibility v3 is enabled
    const handleKeyDown = matchInteractionA11yService.isMatchInteractionA11yEnabled() ? (e) => {
        matchInteractionA11yService.handleRadioButtonKeyDown(e, isReadOnly, checked, handleUserResponse, value);
    } : undefined;

    // Check if v4 accessibility is enabled - if so, use div wrapper instead of label
    const isV4Supported = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v4);

    // Handler for input click - stops propagation to prevent div onClick from firing
    const handleInputClick = (e) => {
        e.stopPropagation();
    };

    // Handler for div click - simulates clicking the radio button (only when clicking empty space)
    const handleDivClick = (e) => {
        // Ignore if click originated from input (shouldn't happen due to stopPropagation, but safety check)
        if (e.target !== e.currentTarget) {
            return;
        }
        if (!isReadOnly) {
            // Focus the input to maintain UX parity with label behavior and enable keyboard navigation
            // This will trigger onFocus which handles navigation/numbering
            if (inputRef.current) {
                inputRef.current.focus();
            }
            // Only simulate a user response when the selection is actually changing
            if (!checked) {
                handleUserResponse({ currentTarget: { value } });
            }
        }
    };

    const radioInput = (
        <input
            ref={inputRef}
            aria-labelledby={labelledby}
            checked={checked}
            disabled={isReadOnly}
            id={id}
            name={name}
            onChange={handleUserResponse}
            onClick={isV4Supported ? handleInputClick : undefined}
            type="radio"
            value={value}
            onFocus={navigateToScorableItem}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            data-question-id={questionId}
        />
    );

    return isV4Supported ? (
        // V4: Use div wrapper with click handler (maintains full-cell clickability without semantic label)
        // Div only expands mouse click area; keyboard interaction is handled by the input element
        <div className={styles.clickableWrapper} onClick={handleDivClick} role="presentation">
            {radioInput}
        </div>
    ) : (
        // Pre-V4: Keep label wrapper for backwards compatibility
        <label htmlFor={id}>{radioInput}</label>
    );
};

RadioButton.propTypes = {
    checked: PropTypes.bool.isRequired,
    columnChoiceIdentifier: PropTypes.string.isRequired,
    handleUserResponse: PropTypes.func.isRequired,
    isInverted: PropTypes.bool.isRequired,
    isReadOnly: PropTypes.bool,
    modelIdentifier: PropTypes.string.isRequired,
    rowChoiceIdentifier: PropTypes.string.isRequired,
    questionId: PropTypes.number.isRequired,
};

export default RadioButton;
